import { useSnapshot } from 'valtio'
import { CurrentUserState } from '@app/storage'
import { useTenantConfig } from '@app/hooks/useTenantConfig'
/**
 * On the demo tenant every new user's profile tabs (followers, profit, stats,
 * engagement) should display data from the pre-seeded demo tipster rather than
 * from their own empty account.
 *
 * This hook returns the demo tipster ID when ALL of the following are true:
 *   1. The current tenant has a `demoProfileId` configured in tenantConfig.
 *   2. The profile being viewed belongs to the currently logged-in user
 *      (i.e. it is the user's own profile page).
 *
 * Otherwise it returns `tipsterId` unchanged, so non-demo tenants and
 * any profile that isn't the logged-in user's are completely unaffected.
 *
 * Usage:
 *   const statsId = useDemoProfileTipsterId(tipsterId)
 *   // use statsId for stat/display queries, keep original tipsterId for actions
 */

export function useDemoProfileTipsterId(tipsterId: string): string {
  const { id: currentUserId } = useSnapshot(CurrentUserState)
  const { getTenantValue } = useTenantConfig()

  const demoProfileId = getTenantValue('demoProfileId', null)


  if (demoProfileId && tipsterId === currentUserId) {
    return demoProfileId
  }
  return tipsterId
}

/** True when `tenantConfig.demoProfileId` is set (Tipsterizer demo tenant). */
export function useIsDemoTenant(): boolean {
  const { getTenantValue } = useTenantConfig()
  return Boolean(getTenantValue('demoProfileId', null))
}
